import React from 'react';
import { toast } from 'react-toastify';
import ActionButton from '../../../Components/Common/ActionButton';
import ChangeRequestList from '../../../Components/crude/ChangeRequestList';
import useChangeRequests from '../../../Components/crude/useChangeRequests';
import { CHANGE_REQUEST_ENDPOINTS } from '../../../Components/crude/changeRequestConstants';
import { crudeApi } from '../../../helpers/crude_api_helper';

// Approval List — second (final) stage of the maker-checker workflow.
// Server filter is fixed to Status == AwaitingApprove. Approve -> the change is
// applied to the real master-data entity and status becomes Approved.
// Reject -> RejectApproval (reverted in the feeder module, status Rejected).
//
// NOTE: the API does not enforce the Crude_Approver permission on these
// endpoints — the gate must be applied on the FE (see summary/contract).
const ApprovalListView = () => {
  const {
    items,
    totalRows,
    loading,
    perPage,
    searchInput,
    setSearchInput,
    setPage,
    setPerPage,
    handleSort,
    refetch,
  } = useChangeRequests({
    endpoint: CHANGE_REQUEST_ENDPOINTS.APPROVAL_LIST,
    detailKey: 'detailApprovals',
  });

  // Action endpoints are [FromForm]. A state-machine violation (row already
  // moved to another status, e.g. a race with another approver) comes back as
  // 422 with the real reason in `detail` (surfaced by the api helper) — safe
  // to show verbatim. A genuine server fault (500) shows a generic message.
  const runAction = async (endpoint, row, notes, successMessage) => {
    try {
      await crudeApi.postForm(endpoint, { requestId: row.id, notes });
      toast.success(successMessage);
    } catch (error) {
      toast.error(`${error}`);
    } finally {
      refetch();
    }
  };

  const renderActions = (row, { confirm }) => (
    <div className='d-flex gap-1 justify-content-center'>
      <ActionButton
        variant='secondary'
        size='sm'
        icon='ri-checkbox-circle-line'
        onClick={() =>
          confirm({
            title: 'Approve Change Request',
            message: `Approve change on "${row.dataKey}"? It will be applied to the master data.`,
            confirmLabel: 'Approve',
            confirmColor: 'success',
            showNotes: true,
            onConfirm: notes =>
              runAction(
                CHANGE_REQUEST_ENDPOINTS.APPROVE,
                row,
                notes,
                'Change request approved and applied'
              ),
          })
        }
      >
        Approve
      </ActionButton>
      <ActionButton
        variant='destructive'
        size='sm'
        confirm={false}
        icon='ri-close-circle-line'
        onClick={() =>
          confirm({
            title: 'Reject Change Request',
            message: `Reject change on "${row.dataKey}"? This will revert it in the feeder module.`,
            confirmLabel: 'Reject',
            confirmColor: 'danger',
            showNotes: true,
            onConfirm: notes =>
              runAction(
                CHANGE_REQUEST_ENDPOINTS.REJECT_APPROVAL,
                row,
                notes,
                'Change request rejected'
              ),
          })
        }
      >
        Reject
      </ActionButton>
    </div>
  );

  return (
    <ChangeRequestList
      title='Approval List'
      cardTitle='Approval List'
      documentTitle='Approval List'
      data={items}
      totalRows={totalRows}
      loading={loading}
      perPage={perPage}
      searchValue={searchInput}
      onSearchChange={setSearchInput}
      onChangePage={setPage}
      onChangeRowsPerPage={newPerPage => {
        setPerPage(newPerPage);
        setPage(1);
      }}
      onSort={handleSort}
      renderActions={renderActions}
      emptyText='No data to approve'
    />
  );
};

export default ApprovalListView;
